import omit from 'lodash/omit';
import omitBy from 'lodash/omitBy';
import isUndefined from 'lodash/isUndefined';
import mergeWith from 'lodash/mergeWith';
import { map, path, pathOr, reduce, sortBy } from 'ramda';
import Moment from 'moment';
import { extendMoment } from 'moment-range';

import { types as sdkTypes } from '../../util/sdkLoader';
import { storableError } from '../../util/errors';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import * as log from '../../util/log';
import { fetchCurrentUserHasListingsSuccess } from '../../ducks/user.duck';
import { overrideArrays } from '../../util/data';
import { updateProfile } from '../ProfileSettingsPage/ProfileSettingsPage.duck';
import { createResourceLocatorString } from '../../util/routes';
import routeConfiguration from '../../routeConfiguration';
import { createSlug } from '../../util/urlHelpers';
import { isNilOrEmpty } from '../../util/validators';
import { entriesAvailabilityPlanTimeBased } from './constants';
import {
  endAfternoonHour,
  utcTimeZone,
  isMorning,
  startAfternoonHour,
  startMorningHour,
} from '../../util/dates';
import { fetchAllTimeSlots } from '../ListingPage/ListingPage.duck';

const moment = extendMoment(Moment);

const { UUID } = sdkTypes;

const requestAction = actionType => params => ({ type: actionType, payload: { params } });

const successAction = actionType => result => ({ type: actionType, payload: result.data });

const errorAction = actionType => error => ({ type: actionType, payload: error, error: true });

// ================ Action types ================ //
export const UPDATE_PERSONAL_INFORMATION_REQUEST = 'app/EditListingPage/UPDATE_PERSONAL_INFORMATION_REQUEST';
export const UPDATE_PERSONAL_INFORMATION_SUCCESS = 'app/EditListingPage/UPDATE_PERSONAL_INFORMATION_SUCCESS';
export const UPDATE_PERSONAL_INFORMATION_ERROR = 'app/EditListingPage/UPDATE_PERSONAL_INFORMATION_ERROR';

export const MARK_TAB_UPDATED = 'app/EditListingPage/MARK_TAB_UPDATED';
export const CLEAR_UPDATED_TAB = 'app/EditListingPage/CLEAR_UPDATED_TAB';

export const CREATE_LISTING_REQUEST = 'app/EditListingPage/CREATE_LISTING_REQUEST';
export const CREATE_LISTING_SUCCESS = 'app/EditListingPage/CREATE_LISTING_SUCCESS';
export const CREATE_LISTING_ERROR = 'app/EditListingPage/CREATE_LISTING_ERROR';

export const UPDATE_LISTING_REQUEST = 'app/EditListingPage/UPDATE_LISTING_REQUEST';
export const UPDATE_LISTING_SUCCESS = 'app/EditListingPage/UPDATE_LISTING_SUCCESS';
export const UPDATE_LISTING_ERROR = 'app/EditListingPage/UPDATE_LISTING_ERROR';

export const SHOW_LISTINGS_REQUEST = 'app/EditListingPage/SHOW_LISTINGS_REQUEST';
export const SHOW_LISTINGS_SUCCESS = 'app/EditListingPage/SHOW_LISTINGS_SUCCESS';
export const SHOW_LISTINGS_ERROR = 'app/EditListingPage/SHOW_LISTINGS_ERROR';

export const UPLOAD_IMAGE_REQUEST = 'app/EditListingPage/UPLOAD_IMAGE_REQUEST';
export const UPLOAD_IMAGE_SUCCESS = 'app/EditListingPage/UPLOAD_IMAGE_SUCCESS';
export const UPLOAD_IMAGE_ERROR = 'app/EditListingPage/UPLOAD_IMAGE_ERROR';

export const UPDATE_IMAGE_ORDER = 'app/EditListingPage/UPDATE_IMAGE_ORDER';

export const CREATE_LISTING_DRAFT = 'app/EditListingPage/CREATE_LISTING_DRAFT';
export const UPDATE_LISTING_DRAFT = 'app/EditListingPage/UPDATE_LISTING_DRAFT';

export const REMOVE_LISTING_IMAGE = 'app/EditListingPage/REMOVE_LISTING_IMAGE';

export const INIT_SLOTS = 'app/EditListingPage/INIT_SLOTS';
export const FETCH_CALENDAR_REQUEST = 'app/EditListingPage/FETCH_CALENDAR_REQUEST';
export const FETCH_CALENDAR_SUCCESS = 'app/EditListingPage/FETCH_CALENDAR_SUCCESS';
export const FETCH_CALENDAR_ERROR = 'app/EditListingPage/FETCH_CALENDAR_ERROR';

export const ADD_SLOTS_REQUEST = 'app/EditListingPage/ADD_SLOTS_REQUEST';
export const ADD_SLOTS_SUCCESS = 'app/EditListingPage/ADD_SLOTS_SUCCESS';
export const ADD_SLOTS_ERROR = 'app/EditListingPage/ADD_SLOTS_ERROR';


// ================ Reducer ================ //

const initialState = {
  // Error instance placeholders for each endpoint
  createListingsError: null,
  updateListingError: null,
  showListingsError: null,
  uploadImageError: null,
  createListingInProgress: false,
  submittedListingId: null,
  redirectToListing: false,
  images: {},
  imageOrder: [],
  removedImageIds: [],
  listingDraft: null,
  updatedTab: null,
  updateInProgress: false,
  hasUpdatedPersonalInformationSuccessfully: false,
  updatePersonalInformationInProgress: false,
  updatePersonalInformationError: false,
  timeslots: {},
  fetchTimeslotsSuccess: false,
  fetchTimeslotsError: false,
  fetchTimeslotsInProgress: false,
  addSlotsSuccess: false,
  addSlotsError: false,
  addSlotsInProgress: false,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case MARK_TAB_UPDATED:
      return { ...state, updatedTab: payload };
    case CLEAR_UPDATED_TAB:
      return { ...state, updatedTab: null, updateListingError: null };

    case CREATE_LISTING_REQUEST:
      return {
        ...state,
        createListingInProgress: true,
        createListingsError: null,
        submittedListingId: null,
        redirectToListing: false,
      };
    case CREATE_LISTING_SUCCESS:
      return {
        ...state,
        createListingInProgress: false,
        submittedListingId: payload.data.id,
        redirectToListing: true,
      };
    case CREATE_LISTING_ERROR:
      return {
        ...state,
        createListingInProgress: false,
        createListingsError: payload,
        redirectToListing: false,
      };

    case UPDATE_LISTING_REQUEST:
      return { ...state, updateInProgress: true, updateListingError: null };
    case UPDATE_LISTING_SUCCESS:
      return { ...state, updateInProgress: false };
    case UPDATE_LISTING_ERROR:
      return { ...state, updateInProgress: false, updateListingError: payload };

    case SHOW_LISTINGS_REQUEST:
      return { ...state, showListingsError: null };
    case SHOW_LISTINGS_SUCCESS:
      return initialState;
    case SHOW_LISTINGS_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, showListingsError: payload, redirectToListing: false };

    case UPLOAD_IMAGE_REQUEST: {
      // payload.params: { id: 'tempId', file }
      const images = {
        ...state.images,
        [payload.params.id]: { ...payload.params },
      };
      return {
        ...state,
        images,
        imageOrder: state.imageOrder.concat([payload.params.id]),
        uploadImageError: null,
      };
    }
    case UPLOAD_IMAGE_SUCCESS: {
      // payload.params: { id: 'tempId', imageId: 'some-real-id'}
      const { id, imageId } = payload;
      const file = state.images[id].file;
      const images = { ...state.images, [id]: { id, imageId, file } };
      return { ...state, images };
    }
    case UPLOAD_IMAGE_ERROR: {
      // eslint-disable-next-line no-console
      const { id, error } = payload;
      const imageOrder = state.imageOrder.filter(i => i !== id);
      const images = omit(state.images, id);
      return { ...state, imageOrder, images, uploadImageError: error };
    }
    case UPDATE_IMAGE_ORDER:
      return { ...state, imageOrder: payload.imageOrder };

    case CREATE_LISTING_DRAFT:
    case UPDATE_LISTING_DRAFT: {
      const { attributes, images } = state.listingDraft || {};
      const updatedImages = payload.images || images;
      return {
        ...state,
        listingDraft: {
          attributes: mergeWith(
            attributes,
            omitBy(payload.attributes, isUndefined),
            overrideArrays,
          ),
          images: updatedImages,
        },
      };
    }

    case REMOVE_LISTING_IMAGE: {
      const id = payload.imageId;

      // Only mark the image removed if it hasn't been added to the
      // listing already
      const removedImageIds = state.images[id]
        ? state.removedImageIds
        : state.removedImageIds.concat(id);

      // Always remove from the draft since it might be a new image to
      // an existing listing.
      const images = omit(state.images, id);
      const imageOrder = state.imageOrder.filter(i => i !== id);

      return { ...state, images, imageOrder, removedImageIds };
    }

    case UPDATE_PERSONAL_INFORMATION_REQUEST:
      return {
        ...state,
        updatePersonalInformationInProgress: true,
        hasUpdatedPersonalInformationSuccessfully: false,
        updatePersonalInformationError: false,
      };
    case UPDATE_PERSONAL_INFORMATION_SUCCESS:
      return {
        ...state,
        updatePersonalInformationInProgress: false,
        hasUpdatedPersonalInformationSuccessfully: true,
        updatePersonalInformationError: false,
      };
    case UPDATE_PERSONAL_INFORMATION_ERROR:
      return {
        ...state,
        updatePersonalInformationInProgress: false,
        hasUpdatedPersonalInformationSuccessfully: false,
        updatePersonalInformationError: true,
      };

    case FETCH_CALENDAR_REQUEST:
      return {
        ...state,
        fetchTimeslotsSuccess: false,
        fetchTimeslotsError: false,
        fetchTimeslotsInProgress: true,
      };
    case FETCH_CALENDAR_SUCCESS:
      return {
        ...state,
        timeslots: {
          ...state.timeslots,
          ...payload.timeslots,
        },
        fetchTimeslotsSuccess: true,
        fetchTimeslotsError: false,
        fetchTimeslotsInProgress: false,
      };
    case FETCH_CALENDAR_ERROR:
      return {
        ...state,
        fetchTimeslotsSuccess: false,
        fetchTimeslotsError: true,
        fetchTimeslotsInProgress: false,
      };

    case INIT_SLOTS:
      return {
        ...state,
        timeslots: {},
      };
    case ADD_SLOTS_REQUEST:
      return {
        ...state,
        addSlotsSuccess: false,
        addSlotsError: false,
        addSlotsInProgress: true,
      };
    case ADD_SLOTS_SUCCESS:
      return {
        ...state,
        addSlotsSuccess: true,
        addSlotsError: false,
        addSlotsInProgress: false,
      };
    case ADD_SLOTS_ERROR:
      return {
        ...state,
        addSlotsSuccess: false,
        addSlotsError: true,
        addSlotsInProgress: false,
      };

    default:
      return state;
  }
}

// ================ Selectors ================ //

// ================ Action creators ================ //
export const initTimeslots = () => ({ type: INIT_SLOTS });
export const fetchTimeslotsRequest = () => ({ type: FETCH_CALENDAR_REQUEST });
export const fetchTimeslotsError = () => ({ type: FETCH_CALENDAR_ERROR });
export const fetchTimeslotsSuccess = (timeslots = {}) => ({
  type: FETCH_CALENDAR_SUCCESS,
  payload: {
    timeslots,
  },
});

export const addSlotsRequest = () => ({ type: ADD_SLOTS_REQUEST });
export const addSlotsError = () => ({ type: ADD_SLOTS_ERROR });
export const addSlotsSuccess = () => ({
  type: ADD_SLOTS_SUCCESS,
});

export const updatePersonalInformationRequest = () => ({ type: UPDATE_PERSONAL_INFORMATION_REQUEST });
export const updatePersonalInformationSuccess = () => ({ type: UPDATE_PERSONAL_INFORMATION_SUCCESS });
export const updatePersonalInformationError = () => ({ type: UPDATE_PERSONAL_INFORMATION_ERROR });

export const markTabUpdated = tab => ({
  type: MARK_TAB_UPDATED,
  payload: tab,
});

export const clearUpdatedTab = () => ({
  type: CLEAR_UPDATED_TAB,
});

export const updateImageOrder = imageOrder => ({
  type: UPDATE_IMAGE_ORDER,
  payload: { imageOrder },
});

export const createListingDraft = listingData => {
  const { images, ...attributes } = listingData;
  return {
    type: CREATE_LISTING_DRAFT,
    payload: {
      attributes,
      images,
    },
  };
};

export const updateListingDraft = listingData => {
  const { images, ...attributes } = listingData;
  return {
    type: UPDATE_LISTING_DRAFT,
    payload: {
      attributes,
      images,
    },
  };
};

export const removeListingImage = imageId => ({
  type: REMOVE_LISTING_IMAGE,
  payload: { imageId },
});

// All the action creators that don't have the {Success, Error} suffix
// take the params object that the corresponding SDK endpoint method
// expects.

// SDK method: ownListings.create
export const createListing = requestAction(CREATE_LISTING_REQUEST);
export const createListingSuccess = successAction(CREATE_LISTING_SUCCESS);
export const createListingError = errorAction(CREATE_LISTING_ERROR);

// SDK method: ownListings.update
export const updateListing = requestAction(UPDATE_LISTING_REQUEST);
export const updateListingSuccess = successAction(UPDATE_LISTING_SUCCESS);
export const updateListingError = errorAction(UPDATE_LISTING_ERROR);

// SDK method: ownListings.show
export const showListings = requestAction(SHOW_LISTINGS_REQUEST);
export const showListingsSuccess = successAction(SHOW_LISTINGS_SUCCESS);
export const showListingsError = errorAction(SHOW_LISTINGS_ERROR);

// SDK method: images.upload
export const uploadImage = requestAction(UPLOAD_IMAGE_REQUEST);
export const uploadImageSuccess = successAction(UPLOAD_IMAGE_SUCCESS);
export const uploadImageError = errorAction(UPLOAD_IMAGE_ERROR);

// ================ Thunk ================ //

export function requestShowCongratPage(actionPayload, history) {
  return (dispatch, getState, sdk) => {
    dispatch(showListings(actionPayload));
    return sdk.ownListings
      .show(actionPayload)
      .then(response => {
        const routes = routeConfiguration();
        const id = pathOr('', ['data', 'data', 'id', 'uuid'], response);
        const slug = createSlug(pathOr('', ['data', 'data', 'attributes', 'title'], response));

        history.push(createResourceLocatorString(
          'CongratPoolPage',
          routes,
          {},
          { id, slug },
        ));
        dispatch(showListingsSuccess(response));
        return response;
      })
      .catch(e => dispatch(showListingsError(storableError(e))));
  };
}

export function requestShowListing(actionPayload) {
  return (dispatch, getState, sdk) => {
    dispatch(showListings(actionPayload));
    return sdk.ownListings
      .show(actionPayload)
      .then(response => {
        // EditListingPage fetches new listing data, which also needs to be added to global data
        dispatch(addMarketplaceEntities(response));
        // In case of success, we'll clear state.EditListingPage (user will be redirected away)
        dispatch(showListingsSuccess(response));
        return response;
      })
      .catch(e => dispatch(showListingsError(storableError(e))));
  };
}

const getNumberOfSeats = isAvailable => isAvailable ? 1 : 0;

export const setEndDateFormattedByDate = date => ({
  year: date.year(),
  month: date.month(),
  date: date.date(),
  hour: isMorning(date) ? startAfternoonHour : endAfternoonHour,
  minute: 0,
  second: 0,
  millisecond: 0,
});

export const setStartDateOnStartingMorning = date => ({
  year: date.year(),
  month: date.month(),
  date: date.date(),
  hour: startMorningHour,
  minute: 0,
  second: 0,
  millisecond: 0,
});

export const setEndDateOnEndingAfternoon = date => ({
  year: date.year(),
  month: date.month(),
  date: date.date(),
  hour: endAfternoonHour,
  minute: 0,
  second: 0,
  millisecond: 0,
});

const deleteAvailabilityExceptions = (sdk, availabilityExceptions = []) => Promise.all(map(({ id }) =>
  sdk.availabilityExceptions.delete({
      id,
    },
    {
      expand: false,
    }), availabilityExceptions),
);

const createAvailabilityException = (sdk, listingId, start, end, isAvailable) => sdk.availabilityExceptions.create({
    listingId,
    start,
    end,
    seats: getNumberOfSeats(isAvailable),
  },
  {
    expand: true,
  });

const createAvailabilityExceptions = (sdk, listingId, ranges = []) => Promise.all(map(({ start, end, seats }) =>
  sdk.availabilityExceptions.create({
    listingId,
    start: start.utc().toISOString(),
    end: end.utc().toISOString(),
    seats,
  }, {
    expand: true,
  }), ranges),
);

const getRangesToOverlaps = (range, availabilityExceptions = []) => reduce((acc, { attributes, id: { uuid } }) => {
  const { start, end, seats } = attributes;
  const rangeToCompare = moment.range(moment(start), moment(end));
  if (!range.overlaps(rangeToCompare)) return acc;

  const availabilityException = {
    id: uuid,
    rangeToCompare,
    seats,
  };
  return [...acc, availabilityException];
}, [], availabilityExceptions);

const sortByUnavailabilities = sortBy(({ seats } = {}) => seats);

const getRangesOverlapped = (range, rangesToOverlaps = [], rangeAskedToCreate) => sortByUnavailabilities(
  reduce((acc, { rangeToCompare, seats }) => {
    const rangesToCreate = rangeToCompare.subtract(range);
    const rangesToCreateWithSeats = map(range => ({
      ...range,
      seats,
    }), rangesToCreate);
    return [...acc, ...rangesToCreateWithSeats];
  }, [rangeAskedToCreate], rangesToOverlaps),
);

const executeCreationAvailabilityException = (dispatch, availabilityExceptionToCreate, fetchingPayload) =>
  availabilityExceptionToCreate.then(response => {
    const {listingId, maxDateShown} = fetchingPayload;
    dispatch(fetchAllTimeSlots(listingId, maxDateShown))
    dispatch(addSlotsSuccess());
  }).catch((e) => {
    console.log('ERROR ', e);
    dispatch(addSlotsError());
  });

export const addAvailabilityExceptionsRequest = (listingId, start, end, isAvailable, maxDateShown) => {
  if (!start || !end || !listingId) return;

  return (dispatch, getState, sdk) => {
    dispatch(addSlotsRequest());
    const fetchingPayload = {
      listingId,
      maxDateShown,
    }
    sdk.availabilityExceptions.query({
      listingId,
      start,
      end,
    }).then(async res => {
      const availabilityExceptions = path(['data', 'data'], res);
      const range = moment.range(start, end);
      if (!availabilityExceptions) return;

      const rangesToOverlaps = getRangesToOverlaps(range, availabilityExceptions);
      if (isNilOrEmpty(rangesToOverlaps)) {
        return executeCreationAvailabilityException(dispatch,
          createAvailabilityException(sdk, listingId, start, end, isAvailable), fetchingPayload);
      }

      await deleteAvailabilityExceptions(sdk, rangesToOverlaps);
      const rangeAskedToCreate = {
        start: moment(start),
        end: moment(end),
        seats: getNumberOfSeats(isAvailable),
      };
      const rangesOverlapped = getRangesOverlapped(range, rangesToOverlaps, rangeAskedToCreate);
      executeCreationAvailabilityException(dispatch,
        createAvailabilityExceptions(sdk, listingId, rangesOverlapped), fetchingPayload);
    });
  };
};

export const addAvailabilityPlanToListingPayload = payload => ({
  ...payload,
  availabilityPlan: {
    type: 'availability-plan/time',
    timezone: utcTimeZone,
    entries: entriesAvailabilityPlanTimeBased,
  },
});

export function requestCreateListing(data, history) {
  return (dispatch, getState, sdk) => {
    dispatch(createListing(data));

    const listingPayload = addAvailabilityPlanToListingPayload(data);
    return sdk.ownListings
      .create(listingPayload)
      .then(response => {
        const id = response.data.data.id.uuid;
        // Modify store to understand that we have created listing and can redirect away
        dispatch(createListingSuccess(response));
        // Fetch listing data so that redirection is smooth
        dispatch(
          requestShowCongratPage({
            id,
            include: ['author', 'images'],
            'fields.image': ['variants.landscape-crop', 'variants.landscape-crop2x'],
          }, history),
        );
        return response;
      })
      .then(response => {
        // We must update the user duck since this might be the first
        // listing for the user, therefore changing the
        // currentUserHasListings flag in the store.
        dispatch(fetchCurrentUserHasListingsSuccess(true));
        return response;
      })
      .catch(e => {
        log.error(e, 'create-listing-failed', { listingData: data });
        return dispatch(createListingError(storableError(e)));
      });
  };
}

// Images return imageId which we need to map with previously generated temporary id
export function requestImageUpload(actionPayload) {
  return (dispatch, getState, sdk) => {
    const id = actionPayload.id;
    dispatch(uploadImage(actionPayload));
    return sdk.images
      .upload({ image: actionPayload.file })
      .then(resp => dispatch(uploadImageSuccess({ data: { id, imageId: resp.data.data.id } })))
      .catch(e => dispatch(uploadImageError({ id, error: storableError(e) })));
  };
}

// Update the given tab of the wizard with the given data. This saves
// the data to the listing, and marks the tab updated so the UI can
// display the state.
export function requestUpdateListing(tab, data) {
  return (dispatch, getState, sdk) => {
    dispatch(updateListing(data));
    const { id } = data;
    let updateResponse;
    return sdk.ownListings
      .update(data)
      .then(response => {
        updateResponse = response;
        const payload = {
          id,
          include: ['author', 'images'],
          'fields.image': ['variants.landscape-crop', 'variants.landscape-crop2x'],
        };
        return dispatch(requestShowListing(payload));
      })
      .then(() => {
        dispatch(markTabUpdated(tab));
        dispatch(updateListingSuccess(updateResponse));
      })
      .catch(e => {
        log.error(e, 'update-listing-failed', { listingData: data });
        return dispatch(updateListingError(storableError(e)));
      });
  };
}

// loadData is run for each tab of the wizard. When editing an
// existing listing, the listing must be fetched first.
export function loadData(params) {
  return dispatch => {
    dispatch(clearUpdatedTab());
    const { id, type } = params;
    if (type === 'new') {
      // No need to fetch anything when creating a new listing
      return Promise.resolve(null);
    }
    const payload = {
      id: new UUID(id),
      include: ['author', 'images'],
      'fields.image': ['variants.landscape-crop', 'variants.landscape-crop2x'],
    };
    return dispatch(requestShowListing(payload));
  };
}

export const updatePersonalInformation =
  (actionPayload, redirectionPayload = {}) => async (dispatch, getState, sdk) => {
    dispatch(updatePersonalInformationRequest());
    const profileUpdate = await dispatch(updateProfile(actionPayload, redirectionPayload));
    if (profileUpdate && profileUpdate.error) {
      dispatch(updatePersonalInformationError());
      return;
    }

    dispatch(updatePersonalInformationSuccess());
  };



// WEBPACK FOOTER //
// ./src/containers/EditListingPage/EditListingPage.duck.js